import { useState, useRef, useCallback } from 'react';
import { inferCallOutcomeFromNotes } from '../utils/inferCallOutcome';
import { isAiConfigured } from '../services/aiService';

/**
 * Beim Verlassen des Notizfelds: Ergebnis aus Notizen ableiten (KI bevorzugt).
 */
export function useCallOutcomeFromNotes({
  currentOutcome,
  setOutcome,
  leadName,
  company,
  skipIfOutcomeChosen = true,
}) {
  const [inferring, setInferring] = useState(false);
  const [inferLabel, setInferLabel] = useState('');
  const [showAmbiguous, setShowAmbiguous] = useState(false);
  const [highlightSlug, setHighlightSlug] = useState(null);
  const lastAnalyzedRef = useRef('');

  const onNotesBlur = useCallback(async (notes) => {
    const text = (notes || '').trim();
    if (text.length < 6) return;
    if (text === lastAnalyzedRef.current) return;
    if (skipIfOutcomeChosen && currentOutcome && currentOutcome !== 'reached_followup') return;

    lastAnalyzedRef.current = text;
    setInferring(true);
    setInferLabel(isAiConfigured() ? 'KI wertet Gespräch aus…' : 'Ergebnis wird erkannt…');
    setShowAmbiguous(false);
    setHighlightSlug(null);

    try {
      const result = await inferCallOutcomeFromNotes(text, { leadName, company });
      if (result.slug) {
        setOutcome(result.slug);
        setHighlightSlug(result.slug);
        setTimeout(() => setHighlightSlug(null), 2200);
      } else if (text.length >= 8) {
        setShowAmbiguous(true);
      }
    } finally {
      setInferring(false);
      setInferLabel('');
    }
  }, [currentOutcome, setOutcome, leadName, company, skipIfOutcomeChosen]);

  const dismissAmbiguous = useCallback(() => setShowAmbiguous(false), []);

  return {
    inferring,
    inferLabel,
    showAmbiguous,
    highlightSlug,
    onNotesBlur,
    dismissAmbiguous,
    resetAnalysis: () => { lastAnalyzedRef.current = ''; },
  };
}
